<template>
  <div>
    <div class="page-head">
      <div>
        <h1 class="page-head__title">{{ t("notices.title") }}</h1>
        <p class="page-head__lede">{{ t("notices.lede") }}</p>
      </div>
      <button type="button" class="btn btn--secondary btn--sm" :disabled="loading" @click="load">{{ t("common.refresh") }}</button>
    </div>

    <div v-if="error" class="notice notice--danger" style="margin-bottom: var(--space-4)"><div class="notice__body">{{ error }}</div></div>

    <div v-if="loading" class="card">
      <div class="row"><span class="spinner" aria-hidden="true"></span><span class="muted small">{{ t("common.loading") }}</span></div>
    </div>
    <div v-else-if="!notices.length" class="card">
      <div class="empty">
        <p class="empty__title">{{ t("notices.emptyTitle") }}</p>
        <p class="small">{{ t("notices.emptyBody") }}</p>
      </div>
    </div>

    <template v-else>
    <article v-for="n in notices" :key="n.id" :class="['card', 'notice-item', `notice-item--${tone(n)}`]">
      <div class="row row--between row--wrap">
        <div class="row">
          <span :class="['badge', TONE_BADGE[tone(n)]]">{{ severityLabel(n) }}</span>
          <h2 class="notice-item__title">{{ n.title || n.kind || n.type }}</h2>
        </div>
        <span class="xs muted">{{ formatDate(n.created_at || n.occurred_at) }}</span>
      </div>
      <p v-if="n.body || n.message" class="small notice-item__body">{{ n.body || n.message }}</p>
      <p v-if="n.app_id" class="xs">
        <router-link :to="{ name: 'app', params: { id: n.app_id } }">{{ n.app_name || n.app_handle || t("notices.openApp") }}</router-link>
      </p>
    </article>
    </template>
  </div>
</template>

<script setup>
import { onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { api } from "../lib/api";
import { useErrorText, formatDate } from "../lib/errors";

const { t, te } = useI18n();
const errorText = useErrorText();

const TONE_BADGE = { info: "badge--info", warning: "badge--warn", critical: "badge--danger", success: "badge--success" };
const TONE_OF = { info: "info", notice: "info", warn: "warning", warning: "warning", error: "critical", critical: "critical", danger: "critical", success: "success" };

const notices = ref([]);
const loading = ref(true);
const error = ref("");

const tone = (n) => TONE_OF[String(n.severity || n.level || "info").toLowerCase()] || "info";
function severityLabel(n) {
  const key = tone(n);
  return te(`notices.severity.${key}`) ? t(`notices.severity.${key}`) : key;
}

async function load() {
  loading.value = true;
  error.value = "";
  try {
    const data = await api.notices();
    notices.value = Array.isArray(data) ? data : data?.items || [];
  } catch (e) {
    error.value = errorText(e);
  } finally {
    loading.value = false;
  }
}

onMounted(load);
</script>

<style scoped>
.notice-item { border-left: 3px solid var(--blue-500); }
.notice-item--warning { border-left-color: var(--amber-500); }
.notice-item--critical { border-left-color: var(--red-500); }
.notice-item--success { border-left-color: var(--green-500); }
.notice-item__title { margin: 0; font-size: var(--text-base); }
.notice-item__body { margin-top: var(--space-2); white-space: pre-line; }
</style>
